// Top banner: polls the platform announcements/maintenance feed and renders active
// announcements + public maintenance windows. Fed by scheduler-service's
// /api/v1/announcements/active (P1-6c). Dismissals persist per-announcement in
// localStorage. Silent on any error — a banner outage must never block the app.
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Info, Wrench, X } from 'lucide-react';
import { gatewayApi } from '@/lib/hrApi';

interface Announcement {
  id: string;
  level: string; // info | warning | critical
  message: string;
  dismissible: boolean;
}
interface Maintenance {
  id: string;
  title: string;
  scheduled_start: string;
  scheduled_end: string;
}

// KaziPeople semantic status utilities (tailwind.config → index.css --color-*):
// a tinted surface (bg-*-soft) with a solid accent border + icon. Full class
// strings per level so Tailwind's JIT scanner picks them up; all flip in dark mode.
const LEVEL_BAR: Record<string, string> = {
  info: 'bg-info-soft border-info',
  warning: 'bg-warning-soft border-warning',
  critical: 'bg-danger-soft border-danger',
};
const LEVEL_ICON: Record<string, string> = {
  info: 'text-info',
  warning: 'text-warning',
  critical: 'text-danger',
};

const DISMISS_KEY = 'kp-dismissed-announcements';

export const PlatformBanner: React.FC = () => {
  const [anns, setAnns] = useState<Announcement[]>([]);
  const [mws, setMws] = useState<Maintenance[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]'));
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    let alive = true;
    const poll = () =>
      gatewayApi
        .get('/api/v1/announcements/active')
        .then((r) => {
          if (!alive) return;
          setAnns(r.data?.announcements || []);
          setMws(r.data?.maintenance || []);
        })
        .catch(() => {});
    poll();
    const t = setInterval(poll, 5 * 60 * 1000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const dismiss = (id: string) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    localStorage.setItem(DISMISS_KEY, JSON.stringify([...next]));
  };

  const visible = anns.filter((a) => !(a.dismissible && dismissed.has(a.id)));
  if (visible.length === 0 && mws.length === 0) return null;

  return (
    <div>
      {mws.map((m) => (
        <div
          key={m.id}
          className={`flex items-center gap-2 border-b px-4 py-1.5 text-sm text-foreground ${LEVEL_BAR.warning}`}
        >
          <Wrench className={`h-4 w-4 shrink-0 ${LEVEL_ICON.warning}`} />
          <span className="flex-1">
            Scheduled maintenance: <strong>{m.title}</strong> — {new Date(m.scheduled_start).toLocaleString()} to{' '}
            {new Date(m.scheduled_end).toLocaleString()}
          </span>
        </div>
      ))}
      {visible.map((a) => {
        const bar = LEVEL_BAR[a.level] || LEVEL_BAR.info;
        const icon = LEVEL_ICON[a.level] || LEVEL_ICON.info;
        return (
          <div key={a.id} className={`flex items-center gap-2 border-b px-4 py-1.5 text-sm text-foreground ${bar}`}>
            {a.level === 'critical' ? (
              <AlertTriangle className={`h-4 w-4 shrink-0 ${icon}`} />
            ) : (
              <Info className={`h-4 w-4 shrink-0 ${icon}`} />
            )}
            <span className="flex-1">{a.message}</span>
            {a.dismissible && (
              <button
                onClick={() => dismiss(a.id)}
                aria-label="Dismiss"
                className="text-muted-foreground opacity-70 hover:opacity-100"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PlatformBanner;
